/**
 * AcceptInvitation — public route hit from the invitation email.
 *   1. GETs /api/suppliers/accept/:token → preview (company, inviter, sector).
 *   2. POSTs {password, name?} → server creates the supplier user, sets
 *      HttpOnly access + CSRF cookies (auto-signed-in), returns the new user.
 *   3. Frontend then navigates to /supplier.
 *
 * No AuthContext dependency during the GET (route is public); after POST
 * we call refreshUser so the SPA hydrates with the freshly-issued session.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Building2, ShieldCheck, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Logo from '@/components/Logo';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ password: '', confirm: '', name: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/suppliers/accept/${token}`);
        if (alive) setInvite(r.data);
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || 'Invitation not found or expired');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await api.post(`/suppliers/accept/${token}`, {
        password: form.password,
        name: form.name || undefined,
      });
      await refreshUser();
      toast.success('Welcome — your supplier workspace is ready');
      navigate('/supplier', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept invitation');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="accept-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="p-8 max-w-md w-full text-center" data-testid="accept-error">
          <div className="text-4xl mb-3">⚠</div>
          <h2 className="text-xl font-heading font-semibold">Invitation problem</h2>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
          <p className="text-xs text-muted-foreground mt-4">Please contact the person who invited you.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo size={44} variant="full" tagline /></div>

        <Card className="p-6 sm:p-8 shadow-lg" data-testid="accept-card">
          <div className="text-center">
            <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <h1 className="text-xl font-heading font-semibold">You&apos;ve been invited to Veridex</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Join <span className="font-medium text-foreground">{invite?.invited_by}</span> as a supplier.
            </p>
          </div>

          <div className="mt-5 p-3 rounded-lg bg-muted/40 border border-border text-sm" data-testid="accept-preview">
            <div className="flex items-center gap-2 text-foreground font-medium">
              <Building2 className="w-4 h-4" /> {invite?.company_name}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {invite?.contact_email} · {invite?.sector} · {invite?.country}
            </div>
          </div>

          <form onSubmit={submit} className="mt-5 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="acc-name">Your name (optional)</Label>
              <Input id="acc-name" data-testid="accept-name-input"
                     placeholder="Hans Mueller" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-pw">Password *</Label>
              <Input id="acc-pw" data-testid="accept-password-input" type="password"
                     placeholder="At least 8 characters" value={form.password}
                     minLength={8} required
                     onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-cf">Confirm password *</Label>
              <Input id="acc-cf" data-testid="accept-confirm-input" type="password"
                     placeholder="Repeat password" value={form.confirm}
                     minLength={8} required
                     onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            </div>
            <Button type="submit" disabled={submitting} className="w-full mt-2" data-testid="accept-submit-btn">
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating account…</>
                : <>Accept & enter workspace <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>

          <p className="text-[11px] text-muted-foreground text-center mt-5">
            By accepting you agree to our <a className="underline" href="/terms">Terms</a> and{' '}
            <a className="underline" href="/privacy">Privacy Policy</a>.
          </p>
        </Card>
      </div>
    </div>
  );
}
